<template>
  <div class="dashboard-teaser">
    <div v-if="context.canSeeShop && show" class="dashboard-teaser__content">
      <div v-if="context.canSeeShopTeaser" class="dashboard-teaser__content">
        <div class="dashboard-teaser-heading" :class="{ intro__step: user?.intro == 2 }">
          <h5 class="title">
            <router-link :to="{ name: 'shop' }" class="text-dark text-uppercase"
              v-html="$t('global.nav.shop')"></router-link>
          </h5>
          <div class="font-12 fw-bold all-products">
            <router-link :to="{ name: 'shop' }" class="text-uppercase">
              <span v-html="$t('dashboard.showAllProducts')"></span>
              <MDBIcon icon="chevron-right" size="1x" />
            </router-link>
          </div>
        </div>

        <MDBRow class="dashboard-teaser__sliders align-items-stretch">
          <MDBCol col="12" xl="4" class="mb-3 mb-lg-0 d-flex" :class="`${user?.intro == 2
            ? 'intro__step'
            : ''
            }`">
            <ProductSlider :slides="bestSoldProducts" :category="$t('dashboard.shop.bestSold')"></ProductSlider>
          </MDBCol>

          <MDBCol col="12" xl="4" class="mb-3 mb-lg-0 d-flex" :class="`${user?.intro == 2
            ? 'intro__step'
            : ''
            }`">
            <ProductSlider :slides="seasonalProducts" :category="$t('dashboard.shop.seasonal')"></ProductSlider>
          </MDBCol>

          <MDBCol col="12" xl="4" class="mb-3 mb-lg-0 d-flex" :class="`${user?.intro == 2
            ? 'intro__step'
            : ''
            }`">
            <ProductSlider :slides="affordableProducts" :category="$t('dashboard.shop.affordable')"></ProductSlider>
          </MDBCol>
        </MDBRow>
      </div>

      <div v-else-if="context.canSeeShopComingSoon" class="dashboard-teaser__content">
        <div class="dashboard-teaser-heading">
          <h5>&nbsp;</h5>
        </div>

        <div v-if="currentLocale" class="banner-container">
          <img class="banner-container__image" :src="require('@/assets/img/shop_closed_nl.png')" alt="" />
          <div class="banner-container__text" v-html="$t('dashboard.shopClosedBanner')" />
        </div>
      </div>
    </div>

    <div v-if="loading">
      <MDBRow>
        <MDBCol col="12" xl="4">
          <Loader :show="loading" />
        </MDBCol>
        <MDBCol col="12" xl="4">
          <Loader :show="loading" />
        </MDBCol>
        <MDBCol col="12" xl="4">
          <Loader :show="loading" />
        </MDBCol>
      </MDBRow>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, onMounted, ref } from 'vue'
import ProductSlider from '@/components/Dashboard/ProductSlider.vue'
import { MDBIcon, MDBCol, MDBRow } from 'mdb-vue-ui-kit'
import Loader from '@/components/General/Loader/index'
import { useItemsConditions } from '@/composables/useItemsConditions'

const store = useStore()

const loading = ref(true)
const show = ref(false)

const user = computed(() => store.getters.getMe)
const currentLocale = computed(() => store.getters.getCurrentLocale)

// Destructure specific context access flags from useItemsConditions
// This provides direct access to individual permission checks
// such as feature toggles or tenant-based availability (e.g. my-bs-app)
const { getContext } = useItemsConditions()
const context = computed(() => getContext())

const bestSoldProducts = computed(() => {
  if (store.getters.getBestSoldProducts) {
    return store.getters.getBestSoldProducts.map((item) => {
      return {
        id: item.id,
        imageUrl: item.imageUrl,
        name: item.name,
        points: item.points,
        variants: item.variants,
        type: item.type,
        has_variants: item.has_variants
      }
    })
  } else {
    return []
  }
})

const seasonalProducts = computed(() => {
  if (store.getters.getSeasonalProducts) {
    return store.getters.getSeasonalProducts.map((item) => {
      return {
        id: item.id,
        imageUrl: item.imageUrl,
        name: item.name,
        points: item.points,
        variants: item.variants,
        type: item.type,
        has_variants: item.has_variants
      }
    })
  } else {
    return []
  }
})

const affordableProducts = computed(() => {
  if (store.getters.getSoonAffordableProducts) {
    return store.getters.getSoonAffordableProducts.map((item) => {
      return {
        id: item.id,
        imageUrl: item.imageUrl,
        name: item.name,
        points: item.points,
        variants: item.variants,
        type: item.type,
        has_variants: item.has_variants
      }
    })
  } else {
    return []
  }
})

onMounted(async () => {
  if (context.value.canSeeShopTeaser && user.value) {
    await store.dispatch("loadExampleProducts", user.value.id)
  }
  loading.value = false
  show.value = true
})
</script>

<style scoped lang="scss">
@import "../../styles/_variables.scss";
@import "@/styles/dashboard_teaser.scss";

.dashboard-teaser,
.dashboard-teaser__content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.dashboard-teaser__sliders {
  flex: 1 1 auto;
  min-height: $dashboardCardHeight;
}

.all-products {
  white-space: nowrap;

  a {
    color: $darkGray;
  }

  i {
    padding-left: 5px;
  }
}

.title {
  &::before {
    content: "";
    display: inline-block;
    width: 27px;
    height: 4px;
    background: #ed1c24;
    transform: rotate(110deg);
    margin: 8px 0;
  }
}

.banner-container {
  position: relative;
  flex: 1 1 auto;
  min-height: $dashboardCardHeight;
  width: 100%;
  overflow: hidden;
}

.banner-container__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.banner-container__text {
  position: absolute;
  top: 0;
  bottom: 0;
  left: clamp(300px, 18vw, 620px);
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin: 0;
  padding: 1rem clamp(1rem, 3vw, 2.5rem);
  text-align: left;
  font-weight: 700;
  line-height: 1.3;
  color: #000;
  font-size: clamp(12px, 2.2vw, 28px);
  pointer-events: none;
}

@media screen and (max-width: $mdBreakpoint) {
  .banner-container__text {
    left: 46%;
    padding: 1rem;
  }
}
</style>
